import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  format,
  getISOWeek,
  getISOWeekYear,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";

/**
 * Returns today's date at local midnight.
 * @returns {Date}
 */
export function getLocalToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Returns yesterday's date at local midnight.
 * @returns {Date}
 */
export function getLocalYesterday() {
  const today = getLocalToday();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
}

/**
 * Returns the date that presets should treat as "today", respecting dateMax.
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {Date}
 */
export function getPresetAnchorDate(dateMax, today = getLocalToday()) {
  if (!dateMax) {
    return today;
  }

  return isBefore(startOfDay(dateMax), today) ? startOfDay(dateMax) : today;
}

/**
 * Clamps a start/end range to optional dateMin/dateMax.
 * @param {Date} start - Range start
 * @param {Date} end - Range end
 * @param {Date} [dateMin] - Optional minimum date
 * @param {Date} [dateMax] - Optional maximum date
 * @returns {{ from: Date, to: Date }}
 */
export function clampRange(start, end, dateMin, dateMax) {
  let from = start;
  let to = end;
  if (dateMin && isAfter(dateMin, from)) {
    from = startOfDay(dateMin);
  }
  if (dateMax && isBefore(dateMax, to)) {
    to = endOfDay(dateMax);
  }
  return { from, to };
}

/**
 * Returns this week's range from Monday through today.
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {{ from: Date, to: Date }}
 */
export function getThisWeekRange(dateMin, dateMax, today = getLocalToday()) {
  const anchorDate = getPresetAnchorDate(dateMax, today);
  const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
  return clampRange(start, endOfDay(anchorDate), dateMin, dateMax);
}

/**
 * Returns this month's range from the 1st of the current month through today.
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {{ from: Date, to: Date }}
 */
export function getThisMonthRange(dateMin, dateMax, today = getLocalToday()) {
  const anchorDate = getPresetAnchorDate(dateMax, today);
  return clampRange(
    startOfMonth(anchorDate),
    endOfDay(anchorDate),
    dateMin,
    dateMax
  );
}

/**
 * Returns last week's range from Monday through Sunday.
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {{ from: Date, to: Date }}
 */
export function getLastWeekRange(dateMin, dateMax, today = getLocalToday()) {
  const anchorDate = getPresetAnchorDate(dateMax, today);
  const start = startOfWeek(subWeeks(anchorDate, 1), { weekStartsOn: 1 });
  const end = endOfDay(addDays(start, 6));
  return clampRange(start, end, dateMin, dateMax);
}

/**
 * Returns last month's full calendar range.
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {{ from: Date, to: Date }}
 */
export function getLastMonthRange(dateMin, dateMax, today = getLocalToday()) {
  const anchorDate = getPresetAnchorDate(dateMax, today);
  const lastMonth = subMonths(anchorDate, 1);
  return clampRange(
    startOfMonth(lastMonth),
    endOfMonth(lastMonth),
    dateMin,
    dateMax
  );
}

/**
 * Returns a rolling N-day range ending on today.
 * @param {number} days - Number of days in the range
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {{ from: Date, to: Date }}
 */
export function getLastDaysRange(
  days,
  dateMin,
  dateMax,
  today = getLocalToday()
) {
  const anchorDate = getPresetAnchorDate(dateMax, today);
  const start = startOfDay(subDays(anchorDate, days - 1));
  return clampRange(start, endOfDay(anchorDate), dateMin, dateMax);
}

/**
 * Returns a rolling N-month range ending on the effective current day.
 * @param {number} months - Number of months in the range
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @param {Date} [today] - Optional current day override for tests
 * @returns {{ from: Date, to: Date }}
 */
export function getLastMonthsRange(
  months,
  dateMin,
  dateMax,
  today = getLocalToday()
) {
  const anchorDate = getPresetAnchorDate(dateMax, today);
  const end = endOfDay(anchorDate);
  const start = startOfDay(addDays(subMonths(anchorDate, months), 1));
  return clampRange(start, end, dateMin, dateMax);
}

/**
 * Returns the month the calendar should display when focusing the end of a range.
 * @param {Date | null | undefined} dateTo - The range end date
 * @returns {Date}
 */
export function getCalendarMonthForRangeEnd(dateTo) {
  return startOfMonth(dateTo || getLocalToday());
}

/**
 * Returns a week-to-date range (Monday through yesterday) for current/previous weeks.
 * @param {number} [weeks=1] - Number of weeks ago to include (1 = this week)
 * @param {Date} [dateMin] - Optional minimum date bound
 * @param {Date} [dateMax] - Optional maximum date bound
 * @returns {{ from: Date, to: Date }}
 */
export function getWeekToDateRange(weeks = 1, dateMin, dateMax) {
  const yesterday = getLocalYesterday();
  const start = startOfWeek(subWeeks(yesterday, weeks - 1), {
    weekStartsOn: 1,
  });
  const end = endOfDay(yesterday);
  return clampRange(start, end, dateMin, dateMax);
}

/**
 * Get all months in a given year
 * @param {number} year - The year for which to get the months
 * @returns {string[]} Array of months in the format 'YYYY-MM'
 */
export const getMonthsInAYear = (year) => {
  const periods = [];
  for (let month = 0; month < 12; month++) {
    const date = new Date(year, month, 1);
    periods.push(
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
    );
  }

  return periods;
};

/**
 * Get the months between two dates
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {string[]} Array of months in the format 'YYYY-MM'
 */
export const getMonthsBetween = (startDate, endDate) => {
  const months = [];
  const currentDate = startOfMonth(startDate);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    months.push(`${year}-${month}`);
    currentDate.setMonth(currentDate.getMonth() + 1); // Move to the next month
  }

  return months;
};

/**
 * Get the weeks between two dates
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {string[]} Array of weeks in the format 'IYYY-IW'
 */
export const getWeeksBetween = (startDate, endDate) => {
  const weeks = [];
  let currentWeekStart = startOfWeek(startDate, {
    weekStartsOn: 1, // Start the week on Monday
  });

  // Loop while the current week's start is before or equal to the end date
  while (
    isBefore(currentWeekStart, endDate) ||
    isEqual(currentWeekStart, endDate)
  ) {
    const weekNumber = getISOWeek(currentWeekStart);
    weeks.push(
      `${getISOWeekYear(currentWeekStart)}-${weekNumber.toString().padStart(2, "0")}`
    );
    currentWeekStart = addWeeks(currentWeekStart, 1);
  }

  return weeks;
};

/**
 * Get the number of days between two dates
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {string[]} Array of days in the format 'YYYY-MM-DD'
 */
export const getDaysBetween = (startDate, endDate) => {
  const days = [];
  const currentDate = startOfDay(startDate);
  const lastDate = startOfDay(endDate);

  while (currentDate <= lastDate) {
    days.push(format(currentDate, "yyyy-MM-dd"));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
};

/**
 * Get the number of months between two dates
 * @param d1 The first date
 * @param d2 The second date
 * @return {number} The number of months between the two dates
 */
export const monthsDiff = (d1, d2 = new Date()) => {
  const months =
    (d2.getFullYear() - d1.getFullYear()) * 12 +
    (d2.getMonth() - d1.getMonth());
  return months < 0 ? 0 : months;
};

export const isRangeWithinBounds = (from, to, dateMin, dateMax) => {
  if (!from || !to) {
    return false;
  }

  if (dateMin && isBefore(startOfDay(from), startOfDay(dateMin))) {
    return false;
  }

  if (dateMax && isAfter(endOfDay(to), endOfDay(dateMax))) {
    return false;
  }

  return true;
};

/**
 * Get comparison dates based on mode
 * @param {Date} from - The start date
 * @param {Date} to - The end date
 * @param {"previousPeriod"|"previousYear"|null} mode - The comparison mode
 * @param {Date} [dateMin] - Optional minimum allowed comparison date
 * @param {Date} [dateMax] - Optional maximum allowed comparison date
 * @returns {{compareFrom: Date|null, compareTo: Date|null}}
 */
export const getComparisonDates = (from, to, mode, dateMin, dateMax) => {
  if (!from || !to) {
    return { compareFrom: null, compareTo: null };
  }

  let compareFrom = null;
  let compareTo = null;

  // previousPeriod and previousYear modes
  if (mode === "previousYear") {
    compareFrom = new Date(from);
    compareFrom.setFullYear(compareFrom.getFullYear() - 1);
    compareTo = new Date(to);
    compareTo.setFullYear(compareTo.getFullYear() - 1);
  }

  if (mode === "previousPeriod") {
    const days = differenceInCalendarDays(to, from);
    compareTo = new Date(from);
    compareTo.setDate(compareTo.getDate() - 1);
    compareFrom = new Date(compareTo);
    compareFrom.setDate(compareFrom.getDate() - days);
  }

  if (!compareFrom || !compareTo) {
    return { compareFrom: null, compareTo: null };
  }

  if (!isRangeWithinBounds(compareFrom, compareTo, dateMin, dateMax)) {
    return { compareFrom: null, compareTo: null };
  }

  return { compareFrom, compareTo };
};
